import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction, toJS } from 'mobx';
import { basedAxios as Axios } from '../../store/Store';
import CustomInput from '../../components/custominput/CustomInput';
import _ from 'lodash';
import { inputValidator } from '../../utils/validation.utils';
import { usePrevious } from '../../hooks/usePrevious';
import { getUrlParam } from '../../utils/url.utils';
import CustomZipDialog from '../../components/custom-zip-dialog/custom-zip-dialog';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

const Field = observer(props => {
  const inputTimerRef = useRef(null);
  const {
    settings,
    environment,
    fields,
    activePersonNo,
    updateOrder,
    contentModuleId,
    pages,
    validations,
    checkConditions,
    checkConditionsOld,
    contentLayoutModuleId,
    current,
    validate,
  } = props;

  const {
    fieldId,
    prefixClassName,
    type,
    readonly,
    required,
    hidden,
    validationType,
    fieldType,
    minLength,
    confirmInput,
    requireUniqueEmail = false,
    disableChangeLock,
    defaultValue,
  } = settings;
  const [isFieldDirty, setIsFieldDirty] = useState(false);
  const orders = toJS(props.orders);
  const field = fields.find(f => f.fieldId == settings.fieldId);

  const validity = useMemo(() => validations.find(v => v.id == contentModuleId), [validations]);

  const [isConfirmValid, setIsConfirmValid] = useState(!confirmInput);
  const [isEmailUnique, setIsEmailUnique] = useState(true);

  const fValue = useMemo(() => {
    const val = toJS(orders)[activePersonNo]?.[_.camelCase(field.type)]?.[_.camelCase(field.name == 'CountryCode' ? 'CountryId' : field.name)];
    return val ?? '';
  }, [orders, activePersonNo, field]);

  const [confirmInputValue, setConfirmInputValue] = useState(fValue);
  const previousValue = usePrevious(fValue);

  useEffect(() => {
    if (previousValue !== fValue) {
      if (inputTimerRef.current) {
        clearTimeout(inputTimerRef.current);
        inputTimerRef.current = null;
      }
      inputTimerRef.current = setTimeout(checkRegisteredEmail, 500);
      if (!isFieldDirty) setConfirmInputValue(fValue);
    }
    if (confirmInput) compareConfirmInput();
    validateField();
  }, [confirmInputValue, fValue, activePersonNo, isConfirmValid, isEmailUnique]);

  useEffect(() => {
    validateField();
  }, [hidden, readonly, required]);

  useEffect(() => {
    setIsFieldDirty(false);
    setConfirmInputValue(fValue ?? '');
  }, [activePersonNo]);

  const handleValueChange = value => {
    setIsFieldDirty(true);
    let newValue = value;
    let order = orders[activePersonNo];

    if (!order) return;

    let newOrder = _.cloneDeep(order);
    let type = newOrder[_.camelCase(field.type)];
    let newType = undefined;

    if (!type) {
      newType = {};
    } else {
      newType = _.cloneDeep(type);
    }
    if (fieldType.toUpperCase()?.startsWith('TELEPHONE') && validationType === 'telephone' && isValidPhoneNumber(newValue, 'SE')) {
      newValue = parsePhoneNumber(newValue, 'SE').formatInternational();
    }
    if (field.name == 'CountryCode') newType[_.camelCase('CountryId')] = newValue;
    else newType[_.camelCase(field.name)] = newValue;
    newOrder[_.camelCase(field.type)] = newType;
    updateOrder(newOrder);
    checkConditionsOld(contentLayoutModuleId);
    checkConditions(`${field.type}.${field.name}`);
  };

  useEffect(() => {
    if (defaultValue && !fValue) {
      handleValueChange(defaultValue);
    }
  }, [defaultValue]);

  const compareConfirmInput = () => {
    if (fValue.toLowerCase().trim() === confirmInputValue.toLowerCase().trim()) {
      if (!isConfirmValid) setIsConfirmValid(true);
    } else {
      if (isConfirmValid) setIsConfirmValid(false);
    }
  };

  const checkRegisteredEmail = useCallback(async () => {
    if (requireUniqueEmail && fieldType.toLowerCase() == 'email' && fValue.length > 0) {
      try {
        const { data } = await Axios.get(
          `./attendee/emailcheck/${environment.eventId}?orderId=${orders?.[activePersonNo]?.orderId}&email=${encodeURIComponent(fValue.toLowerCase().trim())}`
        );
        const existOnOrder = orders?.some(
          o => o.person.email?.toLowerCase().trim() === fValue.toLowerCase().trim() && o.orderId !== orders?.[activePersonNo]?.orderId
        );
        const exists = existOnOrder || data.regExists;

        setIsEmailUnique(!exists);
      } catch (error) {
        console.error(error);
      }
    } else {
      setIsEmailUnique(true);
    }
  }, [fValue, orders, activePersonNo, requireUniqueEmail]);

  const validateField = () => {
    let input = fValue;
    let isValid = true;
    let reason = '';

    if (['FIRSTNAME', 'LASTNAME', 'EMAIL'].includes(fieldType.toUpperCase()) && fValue.length === 0) {
      isValid = false;
      reason = 'required';
    } else if (readonly || hidden) {
      isValid = true;
      reason = '';
    } else if (required === true && input.length === 0) {
      isValid = false;
      reason = 'required';
    } else if (minLength > 0 && input.length < minLength) {
      isValid = false;
      reason = 'minLength';
    } else if (!isConfirmValid) {
      isValid = false;
      reason = 'confirmNotMatching';
    } else if (!isEmailUnique) {
      isValid = false;
      reason = 'emailNotUnique';
    } else if (validationType !== 'none') {
      isValid = inputValidator(input, validationType);
      reason = validationType;
    } else if (fieldType.toUpperCase()?.startsWith('TELEPHONE') && settings?.showCountryCodeSelector && required === true) {
      const parts = input?.split(' ');
      if (parts?.length === 1 || parts?.[1] === '') {
        isValid = false;
        reason = 'required';
      }
    }

    if (fieldType?.toUpperCase() === 'MAILBCC' && input.length > 0) {
      const bccRegex = /^(?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(?:;[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})*$/;
      isValid = !!input.match(bccRegex);

      if (!isValid) {
        reason = 'invalidFormat';
      }
    }

    runInAction(() => {
      if (validity) {
        validity.valid = isValid;
        validity.reason = reason;
      }
    });
  };

  useEffect(() => {
    if (orders[activePersonNo]?.badgeNo) {
      validateField();
    }
  }, [orders, validateField]);

  const isValid = useMemo(() => {
    if (!disableChangeLock && orders?.[0]?.badgeNo) return true;
    if (fieldType.toUpperCase()?.startsWith('TELEPHONE') && settings?.showCountryCodeSelector && !validate) {
      const parts = fValue?.split(' ');

      if (parts?.length > 1 && parts?.[1] === '') {
        return false;
      } else {
        return undefined;
      }
    } else if (fValue.length > 0 && validity?.valid === true) return true;
    if (fValue.length > 0 && validity?.valid === false) return false;
    else if (validate && validations.find(v => v.id === contentModuleId)?.valid === false) return false;
    return undefined;
  }, [
    validity,
    validate,
    validations,
    disableChangeLock,
    fValue,
    validateField,
    contentModuleId,
    fieldType,
    fieldType,
    settings?.showCountryCodeSelector,
    orders,
  ]);

  const isChangeLocked = Boolean(!disableChangeLock && orders?.[activePersonNo]?.badgeNo);

  if (hidden) return null;

  return (
    <>
      <CustomZipDialog {...props} />
      <CustomInput
        moduleType={'FIELD'}
        moduleId={contentLayoutModuleId}
        prefixClassName={prefixClassName + ` ${type}`}
        autocomplete={fields.find(f => f.fieldId === fieldId)?.fieldLanguage?.find(fl => fl.languageCode === environment.lang)?.label || settings.defaultLabel}
        type={fieldType}
        readonly={readonly || isChangeLocked}
        value={fValue}
        required={minLength > 0 || required || ['email', 'firstname', 'lastname'].includes(fieldType.toLowerCase())}
        lang={environment.lang}
        eventId={environment.eventId}
        showConfirmInput={confirmInput && !isChangeLocked && !readonly}
        settings={settings}
        confirmInputValue={confirmInputValue}
        confirmInputOnChange={e => setConfirmInputValue(e.target.value)}
        valid={isValid}
        onChange={e => handleValueChange(e.target ? e.target.value : e)}
        label={fields.find(f => f.fieldId === fieldId)?.fieldLanguage?.find(fl => fl.languageCode === environment.lang)?.label || settings.defaultLabel}
        subLabel={fields.find(f => f.fieldId === fieldId)?.fieldLanguage?.find(fl => fl.languageCode === environment.lang)?.subLabel}
      />
    </>
  );
});

export default Field;
